import { z } from '../zod'
import { applyCheck } from './checks'
import { type FieldJSON, type ParamsSchemaJSON, SCHEMA_JSON_VERSION } from './types'
import { fromJsonValue } from './values'

// Wire → zod. Rebuilt through the PUBLIC zod API (and through the FlowRunner-extended `z`, so `.meta()`
// carries `frWidget` / `frDictionary` / `frLabels` back), which is what makes the result indistinguishable from
// the author's own schema as far as the pipeline is concerned.

type AnySchema = { meta?: (value: Record<string, unknown>) => AnySchema }

// A zod factory that takes an optional message, as every one of these does.
type Factory = (message?: string) => unknown

/**
 * `format` → the constructor that produces it.
 *
 * Rebuilt with the real helper rather than as `z.string().regex(...)`, because the pipeline reads the format
 * token back off the schema — it is what picks the widget and what words an `invalid_format` failure.
 *
 * Guarded with `typeof`, so a token this zod release does not know degrades to a plain string instead of
 * throwing at decode time. Stored payloads outlive the code that wrote them.
 */
const FORMAT_FACTORIES: Record<string, Factory | undefined> = {
  url      : z.url,
  email    : z.email,
  hostname : z.hostname,
  e164     : z.e164,
  emoji    : z.emoji,
  uuid     : z.uuid,
  guid     : z.guid,
  nanoid   : z.nanoid,
  cuid     : z.cuid,
  cuid2    : z.cuid2,
  ulid     : z.ulid,
  xid      : z.xid,
  ksuid    : z.ksuid,
  base64   : z.base64,
  base64url: z.base64url,
  hex      : z.hex,
  jwt      : z.jwt,
  ipv4     : z.ipv4,
  ipv6     : z.ipv6,
  cidrv4   : z.cidrv4,
  cidrv6   : z.cidrv6,
  // z.iso.* — validated STRINGS, unlike the `date` core type below.
  datetime : z.iso?.datetime,
  date     : z.iso?.date,
  time     : z.iso?.time,
  duration : z.iso?.duration,
}

function decodeString(field: FieldJSON): unknown {
  const factory = field.format ? FORMAT_FACTORIES[field.format] : undefined

  return typeof factory === 'function' ? factory(field.message) : z.string(field.message)
}

function decodeBase(field: FieldJSON): unknown {
  switch (field.type) {
    case 'string':
      return decodeString(field)

    case 'number':
      // `z.int()` is `z.number()` carrying the `safeint` format, and the pipeline reads that token back.
      return field.format === 'safeint' || field.format === 'int32' ? z.int(field.message) : z.number(field.message)

    case 'bigint':
      return z.bigint(field.message)

    case 'boolean':
      return z.boolean(field.message)

    case 'date':
      return z.date(field.message)

    case 'enum':
      return z.enum((field.values ?? []).map(fromJsonValue) as string[], field.message)

    case 'literal':
      return z.literal((field.values ?? []).map(fromJsonValue) as (string | number | boolean)[], field.message)

    case 'object':
      return z.object(Object.fromEntries((field.properties ?? []).map(inner => [inner.name, decodeField(inner)])))

    case 'array':
      return z.array((field.element ? decodeField(field.element) : z.unknown()) as never)

    // Every branch, so the rebuild accepts everything the author's union does. A single branch would make
    // the console's form stricter than execution, which ./types.ts forbids in that direction.
    case 'union': {
      const options = (field.options ?? []).map(decodeField)

      return options.length ? z.union(options as never) : z.unknown()
    }

    // Keys are open, as they are in the declaration: only the VALUES were modelled (see ./types.ts), so the
    // rebuild accepts any string key and stays no stricter than the author's.
    case 'record':
      return z.record(z.string(), (field.value ? decodeField(field.value) : z.unknown()) as never)

    default:
      // A field the encoder could not model validates nothing rather than rejecting everything — the
      // decoded schema is never stricter than the author's (see ./types.ts).
      return z.unknown()
  }
}

/**
 * One field, rebuilt.
 *
 * Order is load-bearing:
 *   1. the base type          — a format helper, not a hand-rolled regex
 *   2. the author's checks    — `.min()`, `.regex()`, …
 *   3. `.meta()` LAST of the three, because a check clones the schema and metadata does not follow the clone
 *   4. the modifiers, canonically: nullable → optional → default
 *
 * Step 4's canonical order is why the wire form can record modifiers as flags — see ./encode.ts.
 */
export function decodeField(field: FieldJSON): unknown {
  let schema = decodeBase(field)

  for (const check of field.checks ?? []) {
    schema = applyCheck(schema, check, field.type)
  }

  if (field.meta) {
    schema = (schema as AnySchema).meta?.(fromJsonValue(field.meta as never) as Record<string, unknown>) ?? schema
  }

  const modifiers = schema as {
    nullable: () => typeof modifiers
    optional: () => typeof modifiers
    default: (value: unknown) => typeof modifiers
  }

  let wrapped = modifiers

  if (field.nullable) {
    wrapped = wrapped.nullable()
  }

  if (field.optional) {
    wrapped = wrapped.optional()
  }

  if (field.default !== undefined) {
    wrapped = wrapped.default(fromJsonValue(field.default))
  }

  return wrapped
}

/**
 * A whole stored schema, rebuilt as the `z.object` every slot must be. Nothing usable in ⇒ `undefined` out,
 * which callers already treat as "this slot declares nothing".
 *
 * An envelope whose `v` this build does not know is refused rather than read as if it were the current
 * shape. Stored payloads outlive the code that wrote them — a definition is encoded once at deploy time and
 * decoded by whatever is deployed later — so a future `v: 2` would otherwise be silently misread field by
 * field. Refusing degrades to "no schema" and the caller falls back to the legacy projection, which is the
 * same direction every other loss in this codec takes (see ./types.ts).
 */
export function decodeParams(json: ParamsSchemaJSON | null | undefined): unknown {
  if (!json?.fields || json.v !== SCHEMA_JSON_VERSION) {
    return undefined
  }

  return z.object(Object.fromEntries(json.fields.map(field => [field.name, decodeField(field)])))
}
